<template>
    <Popper class="hrm_task_assignee" :disabled="disabled" :placement="placement" @open:popper="popperOpened">
        <button class="w-full hrm_option_selection_box">
            <span v-if="optionLabel"
                class="relative flex items-center gap-2 w-full h-full hrm_task_has_priority px-4 justify-between">
                <slot name="selectItem" :item="optionLabel">
                    <div class="flex items-center gap-2">
                        <span :style="{ color: optionLabel.color }">
                        </span>
                        <span class="flex-none hrm_single_label">
                            {{ optionLabel?.[label] }}</span>
                    </div>
                </slot>

                <template v-if="!disabled">
                    <button @click="clearValues()" class="hrm_assigned_clear" v-if="canClear">
                        <Icon icon="mingcute:close-fill" width="1.2em" height="1.2em" style="color: white"
                            class="cross_icon" />
                    </button>
                </template>
            </span>
            <slot v-else name="noItem" :text="noItem || 'Select item'">
                <span class="w-full flex items-center gap-2 text-start hrm_no_option">
                    {{ noItem || "Select item" }}
                </span>
            </slot>
        </button>
        <template #content>
            <div v-if="isSearch" class="flex items-center hrm_option_search">
                <!-- <Icon class="flex-none" icon="ic:outline-search" width="18px" height="18px" /> -->
                <input ref="searchInputRef" type="text" v-model="searchItem" :placeholder="searchInputPlaceholder || 'Search...'" />
                <button v-if="searchItem" @click="searchItem = ''" class="px-1 py-1">
                    <Icon icon="mingcute:close-fill" width="1.2em" height="1.2em" style="color: white" />
                </button>
            </div>
            <div class="hrm_options_list">
                <ul v-if="filteredOptions && filteredOptions.length > 0">
                    <template v-for="option in filteredOptions" :key="option">
                        <li v-if="showOption(option)" class="cursor-pointer hrm_option_item"
                            @click="setOptionValue(option)">
                            <div class="flex items-center gap-2">
                                <slot name="option" :option="option" :selectedValue="selectedValue">
                                    <div class="w-full flex justify-between">
                                        <span>
                                            {{ option?.[label] }}
                                        </span>
                                        <span v-if="
                                            option?.[valueProp] ==
                                            selectedValue?.[valueProp]
                                        ">
                                            <Icon icon="mingcute:check-fill" width="1.2em" height="1.2em"
                                                style="color: #41ff41" />
                                        </span>
                                    </div>
                                </slot>
                            </div>
                        </li>
                    </template>
                </ul>
                <div class="h-8 flex items-center justify-center hrm_no_options" v-else>
                    <span>{{ noResultFound || "No item found" }}</span>
                </div>
            </div>
        </template>
    </Popper>
</template>
<script setup>
import { ref, computed, watch, onMounted, nextTick } from "vue";
import Popper from "vue3-popper";
import { Icon } from "@iconify/vue";

const emits = defineEmits(["update:modelValue", "select", "clear"]);
const props = defineProps({
  options: {
    type: Array,
    default: () => [] // empty array as default
  },
  label: {
    type: String,
    default: ''
  },
  valueProp: {
    type: String,
    default: ''
  },
  modelValue: {
    type: [String, Number, Object],
    default: null
  },
  disabled: {
    type: Boolean,
    default: false
  },
  canClear: {
    type: Boolean,
    default: false
  },
  noItem: {
    type: String,
    default: 'No items'
  },
  noResultFound: {
    type: String,
    default: 'No results found'
  },
  searchInputPlaceholder: {
    type: String,
    default: 'Search...'
  },
  icon: {
    type: String,
    default: ''
  },
  disabledOption: {
    type: String,
  },
  placement: {
    type: String,
    default: 'bottom'
  },
  isSearch: {
    type: Boolean,
    default: true
  }
})
const searchItem = ref();
const selectedValue = ref(null);
const searchInputRef = ref(null);

//Task assigned data and methods
const filteredOptions = computed(() => {
    if (!searchItem.value) return props.options;
    return props.options.filter((option) =>
        option?.[props.label]
            .toLowerCase()
            .includes(searchItem.value.toLowerCase())
    );
});

const optionLabel = computed(() => {
    if (!props.options || props.modelValue === undefined) {
        return null;
    }

    return props.options.find(
        (option) => option?.[props.valueProp] == props.modelValue
    );
});

// Set or clear task data
const setOptionValue = (option) => {
    selectedValue.value = option;
    emits("update:modelValue", option?.[props.valueProp]);
    emits("select", option?.[props.valueProp]);
};

const clearValues = () => {
    selectedValue.value = null;
    emits("update:modelValue", null);
    emits("clear");
};

const getOption = () => {
    if (props.modelValue === undefined) {
        return null;
    }
    const option = props.options.find(
        (option) => option?.[props.valueProp] == props.modelValue
    );
    selectedValue.value = option;
};

const showOption = (option) => {
    if (!props.disabledOption) return true;
    else {
        if (props.disabledOption == option.id) {
            return false;
        } else {
            return true;
        }
    }
};

const popperOpened = () => {
    nextTick(() => {
        if (searchInputRef.value) {
            searchInputRef.value.focus();
        }
    })
}

onMounted(() => {
    getOption();
});
</script>
<style scoped>
.hrm_options_list {
    padding: 4px 6px;
    max-height: 150px;
    overflow-y: auto;
}

.hrm_option_item {
    color: #8893a3;
    font-size: 14px;
    padding: 2px 4px;
    border-radius: 6px;
}

.hrm_single_label {
    color: #8893a3;
    font-size: 12px;
}

.dark .hrm_options_list .hrm_option_item:hover {
    background: #3d454f;
}

.hrm_options_list ul {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.hrm_option_selection_box {
    background: #35373b !important;
    border-radius: 6px;
}

.hrm_option_selection_box::placeholder {
    color: #a3a3a3 !important;
}

.hrm_no_option {
    font-size: 14px;
    padding: 6px 10px;
    color: #b2b9c3;
}

.hrm_option_search {
    padding: 8px 6px;
}

.dark .hrm_option_search {
    border-bottom: 1px solid #48505a;
    color: #c8ccd1;
}

.hrm_option_search input {
    width: 100%;
    padding: 6px 10px;
    font-size: 14px;
    line-height: 1;
    background: #4b5461;
    border-radius: 6px;
    margin: 0 6px;
    color: #c8ccd1;
}

.hrm_option_search input::placeholder {
    color: #c9ced4 !important;
}

.hrm_no_options {
    color: #9da5af;
    font-size: 13px;
    font-weight: 500;
}

.hrm_task_assignee .hrm_assigned_clear .cross_icon {
    display: flex !important;
    justify-content: end !important;
    align-items: end;
}
</style>
